import { warnAboutMissingStyles } from '@entur/utils';
import React from 'react';
import classNames from 'classnames';
import { LoadingDots } from '@entur/loader';
import { CloseSmallIcon } from '@entur/icons';
import { Fieldset } from '@entur/form';
function _extends() {
  _extends = Object.assign ? Object.assign.bind() : function (target) {
    for (var i = 1; i < arguments.length; i++) {
      var source = arguments[i];
      for (var key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
          target[key] = source[key];
        }
      }
    }
    return target;
  };
  return _extends.apply(this, arguments);
}
function _objectWithoutPropertiesLoose(source, excluded) {
  if (source == null) return {};
  var target = {};
  var sourceKeys = Object.keys(source);
  var key, i;
  for (i = 0; i < sourceKeys.length; i++) {
    key = sourceKeys[i];
    if (excluded.indexOf(key) >= 0) continue;
    target[key] = source[key];
  }
  return target;
}
var ChoiceChipGroupContext = /*#__PURE__*/React.createContext(null);
var ChoiceChipGroupContextProvider = ChoiceChipGroupContext.Provider;
var useChoiceChipGroupContext = function useChoiceChipGroupContext() {
  var context = React.useContext(ChoiceChipGroupContext);
  if (!context) {
    throw new Error('You need to wrap your ChoiceChips in a ChoiceChipGroup-component');
  }
  return context;
};
var _excluded$4 = ["className", "children", "value", "disabled", "style", "size"];
var ChoiceChip = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    children = _ref.children,
    value = _ref.value,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    style = _ref.style,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$4);
  var childrenArray = React.Children.toArray(children);
  var isIcon = function isIcon(child) {
    var _child$type;
    return child == null ? void 0 : (_child$type = child.type) == null ? void 0 : _child$type.toString().toLowerCase().includes('icon');
  };
  var hasLeadingIcon = childrenArray.length > 1 && isIcon(childrenArray.at(0));
  var hasTrailingIcon = childrenArray.length > 1 && isIcon(childrenArray.at(-1));
  var classList = classNames(className, 'eds-chip', "eds-chip--size-" + size, {
    'eds-chip--disabled': disabled,
    'eds-chip--leading-icon': hasLeadingIcon,
    'eds-chip--trailing-icon': hasTrailingIcon
  });
  var _useChoiceChipGroupCo = useChoiceChipGroupContext(),
    name = _useChoiceChipGroupCo.name,
    selectedValue = _useChoiceChipGroupCo.value,
    onChange = _useChoiceChipGroupCo.onChange;
  return React.createElement("label", {
    className: "eds-choice-chip",
    style: style
  }, React.createElement("input", _extends({
    className: "eds-choice-chip__input",
    type: "radio",
    name: name,
    ref: ref,
    value: value,
    disabled: disabled,
    checked: selectedValue === value,
    onChange: onChange,
    "aria-disabled": disabled
  }, rest)), React.createElement("div", {
    className: classList
  }, children));
});
var _excluded$3 = ["children", "className", "loading", "size"];
var ActionChip = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    _ref$loading = _ref.loading,
    loading = _ref$loading === void 0 ? false : _ref$loading,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$3);
  var childrenArray = React.Children.toArray(children);
  var isIcon = function isIcon(child) {
    var _child$type;
    return child == null ? void 0 : (_child$type = child.type) == null ? void 0 : _child$type.toString().toLowerCase().includes('icon');
  };
  var hasLeadingIcon = childrenArray.length > 1 && isIcon(childrenArray.at(0));
  var hasTrailingIcon = childrenArray.length > 1 && isIcon(childrenArray.at(-1));
  var ariaLabelValue = function ariaLabelValue() {
    if (rest['aria-label']) return rest['aria-label'];
    if (loading) return ariaLabelWhenLoading;
    return undefined;
  };
  var ariaLabelWhenLoading = childrenArray.filter(function (child) {
    return typeof child === 'string';
  }).join(' ');
  var classList = classNames(className, 'eds-chip', 'eds-action-chip', "eds-chip--size-" + size, {
    'eds-chip--leading-icon': hasLeadingIcon,
    'eds-chip--trailing-icon': hasTrailingIcon,
    'eds-action-chip--disabled': rest.disabled
  });
  var actionChip = React.createElement("button", _extends({
    className: classList,
    ref: ref,
    "aria-busy": loading,
    "aria-label": ariaLabelValue(),
    type: "button"
  }, rest), loading ? React.createElement(LoadingDots, {
    className: "eds-action-chip__loading-dots"
  }) : children);
  if (rest.disabled) {
    return React.createElement("div", {
      className: "eds-action-chip--disabled__wrapper"
    }, actionChip);
  }
  return React.createElement(React.Fragment, null, actionChip);
});
var _excluded$2 = ["children", "className", "onClose", "closeButtonAriaLabel", "size"];
var TagChip = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var children = _ref.children,
    className = _ref.className,
    onClose = _ref.onClose,
    closeButtonAriaLabel = _ref.closeButtonAriaLabel,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$2);
  var selectedCloseLabel = closeButtonAriaLabel != null ? closeButtonAriaLabel : 'Fjern ' + (children == null ? void 0 : children.toString());
  return React.createElement("div", _extends({
    className: classNames('eds-chip', 'eds-tag-chip', "eds-chip--size-" + size, className)
  }, rest), children, React.createElement("button", {
    className: "eds-tag-chip__close-button",
    type: "button",
    onClick: onClose,
    "aria-label": selectedCloseLabel,
    ref: ref
  }, React.createElement(CloseSmallIcon, {
    "aria-hidden": true
  })));
});
var _excluded$1 = ["className", "children", "value", "disabled", "name", "style", "size"];
var FilterChip = /*#__PURE__*/React.forwardRef(function (_ref, ref) {
  var className = _ref.className,
    children = _ref.children,
    value = _ref.value,
    _ref$disabled = _ref.disabled,
    disabled = _ref$disabled === void 0 ? false : _ref$disabled,
    name = _ref.name,
    style = _ref.style,
    _ref$size = _ref.size,
    size = _ref$size === void 0 ? 'medium' : _ref$size,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded$1);
  var classList = classNames(className, 'eds-filter-chip');
  return React.createElement("label", {
    className: classList,
    style: style
  }, React.createElement("input", _extends({
    className: "eds-filter-chip__input",
    type: "checkbox",
    name: name,
    ref: ref,
    value: value,
    disabled: disabled
  }, rest)), React.createElement("div", {
    className: "eds-chip " + (size ? "eds-chip--size-" + size : '')
  }, React.createElement("span", {
    className: "eds-filter-chip__icon"
  }, React.createElement(CheckboxIcon, null)), children));
});
var CheckboxIcon = function CheckboxIcon() {
  return React.createElement("svg", {
    className: "eds-filter-chip-icon",
    width: "16",
    height: "16",
    viewBox: "0 0 16 16",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg"
  }, React.createElement("path", {
    className: "eds-filter-chip-icon__path",
    d: "M1.71283 7.10801L5.6464 11.1377C5.84098 11.3371 6.16095 11.339 6.35786 11.1419L14.2916 3.20325",
    stroke: "#181C56",
    strokeWidth: "2"
  }));
};
var _excluded = ["name", "value", "children", "onChange", "label"];
var ChoiceChipGroup = function ChoiceChipGroup(_ref) {
  var name = _ref.name,
    value = _ref.value,
    children = _ref.children,
    onChange = _ref.onChange,
    label = _ref.label,
    rest = _objectWithoutPropertiesLoose(_ref, _excluded);
  var contextValue = React.useMemo(function () {
    return {
      name: name,
      value: value,
      onChange: onChange
    };
  }, [name, value, onChange]);
  return React.createElement(ChoiceChipGroupContextProvider, {
    value: contextValue
  }, React.createElement(Fieldset, _extends({
    className: "eds-choice-chips-group",
    label: label
  }, rest), children));
};
warnAboutMissingStyles('chip', 'form');
export { ActionChip, ChoiceChip, ChoiceChipGroup, FilterChip, TagChip };